import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

import { MagicStructureMember } from './magic-structure-member.model';
import { ICloneAndCopy } from '../../interfaces/clone-and-copy';

// Define a constant.
const MEMBERSHIP_NAME_FORM_CONTROL_NAME: string = 'membershipName';
const MEMBERSHIP_STRUCTURE_NAME_FORM_CONTROL_NAME: string = 'membershipStructureName';
const MEMBERSHIP_ROLE_NAMES_FORM_CONTROL_NAME: string = 'membershipRoleNames';

export class MagicStructureMembership {
    // Define primitive properties.
    public name: string; // The name associated with this membership.

    public structureName: string; // A folder or form instance name.

    public roleNames: string[]; // The role associated with this membership.

    public membershipSelected: boolean = false; // i.e. selected in the UI.
    public membershipDetailsExpanded: boolean = false; // i.e. expanded/showing details in the UI.
    public membershipFulfilled: boolean = false;

    // Define contained models.
    public members: MagicStructureMember[];

    // Constructor.
    public constructor() {
        return;
    }

    // Method to create an example memebership.
    public static createExampleMembership(structureType: string, availableRoleNames: string[]): MagicStructureMembership {
        let membership: MagicStructureMembership = new MagicStructureMembership();

        membership.name = `Example ${structureType}`;
        membership.structureName = `Magic ${structureType} 1`;

        membership.roleNames = [];
        for (let iRole: number = 0; iRole < availableRoleNames.length; iRole++) {
            if (iRole % 2 == 0)
                membership.roleNames.push(availableRoleNames[iRole]);
        }

        membership.members = MagicStructureMember.createExampleMembers();;

        return membership;
    }

    // ICloneAndCopy methods.
    public clone(): ICloneAndCopy {
        let clone: MagicStructureMembership = new MagicStructureMembership();
        clone.copy(this);

        return clone;
    }

    public copy(objectToCopy: ICloneAndCopy): void {
        let membershipToCopy: MagicStructureMembership = <MagicStructureMembership>objectToCopy;

        this.name = membershipToCopy.name;
        this.structureName = membershipToCopy.structureName;

        this.roleNames = membershipToCopy.roleNames;

        this.membershipSelected = membershipToCopy.membershipSelected;
        this.membershipDetailsExpanded = membershipToCopy.membershipDetailsExpanded;
        this.membershipFulfilled = membershipToCopy.membershipFulfilled;

        this.members = [];
        if (membershipToCopy.members != null) {
            for (let iMember: number = 0; iMember < membershipToCopy.members.length; iMember++) {
                let member: MagicStructureMember = membershipToCopy.members[iMember];
                let memberClone = <MagicStructureMember>member.clone();

                this.members.push(memberClone);
            }
        }
    }
}

export class MagicStructureMembershipWithFormGroup extends MagicStructureMembership {
    // Define form controls.
    //public structureNameFormControl: FormControl = null;
    private _formBuilder: UntypedFormBuilder = null;
    private membershipFormGroup: UntypedFormGroup = null; // We are using a form group to match the rest of the magic folder/form instance code.
    private availableRoleNames: string[] = null;

    // Constructor.
    public constructor(_formBuilder: UntypedFormBuilder, availableRoleNamesParam: string[]) {
        super();

        this._formBuilder = _formBuilder;
        this.availableRoleNames = availableRoleNamesParam;

        this.roleNames = this.availableRoleNames;
    }

    // Form management.
    public createFormGroup(): void {
        this.membershipFormGroup = this._formBuilder.group({
            membershipName: [{ value: this.name, disabled: false }, Validators.required],
            membershipStructureName: [{ value: this.structureName, disabled: false }, Validators.required],
            membershipRoleNames: [{ value: this.roleNames, disabled: false }, Validators.required]
        });

        let membershipNameCtl: UntypedFormControl = <UntypedFormControl>this.membershipFormGroup.get(MEMBERSHIP_NAME_FORM_CONTROL_NAME);
        membershipNameCtl.valueChanges.subscribe(value => {
            this.name = value;
        });
        let membershipStructureNameCtl: UntypedFormControl = <UntypedFormControl>this.membershipFormGroup.get(MEMBERSHIP_STRUCTURE_NAME_FORM_CONTROL_NAME);
        membershipStructureNameCtl.valueChanges.subscribe(value => {
            this.structureName = value;
        });
        let membershipRoleNamesCtl: UntypedFormControl = <UntypedFormControl>this.membershipFormGroup.get(MEMBERSHIP_ROLE_NAMES_FORM_CONTROL_NAME);
        membershipRoleNamesCtl.valueChanges.subscribe(value => {
            this.roleNames = value;
        })

        return;
    }

    public updateAvailableRoleNames(availableRoleNamesParam: string[]): void {
        let roleNamesToKeep: string[] = [];

        if ((this.roleNames != null) && (this.roleNames.length > 0)) {
            for (let iRole: number = 0; iRole < this.roleNames.length; iRole++) {
                let roleName: string = this.roleNames[iRole];

                let foundRole: string = availableRoleNamesParam.find(r => r == roleName);
                if (foundRole != null)
                    roleNamesToKeep.push(foundRole);
            }
        }

        this.roleNames = roleNamesToKeep;

        this.availableRoleNames = availableRoleNamesParam;
    }

    // addRoleNames():  add role names, checking that each is available.
    public setRoleNames(roleNamesParam: string[]): void {
        this.roleNames = [];

        for (let iRole: number = 0; iRole < roleNamesParam.length; iRole++) {
            let roleName: string = roleNamesParam[iRole];

            if (this.availableRoleNames.find(r => r == roleName) != null)
                this.roleNames.push(roleName);
        }
    }

    // Methods used by HTML code.
    public get MembershipFormGroup(): UntypedFormGroup {
        return this.membershipFormGroup;
    }

    public get AvailableRoleNames(): string[] {
        return this.availableRoleNames;
    }

    // Type conversion.
    public asMagicStructureMembership(): MagicStructureMembership {
        let membershipWithoutFormGroup: MagicStructureMembership = new MagicStructureMembership();

        membershipWithoutFormGroup.name = this.name;

        membershipWithoutFormGroup.structureName = this.structureName; // Not yet being used.

        membershipWithoutFormGroup.roleNames = this.roleNames;

        membershipWithoutFormGroup.membershipSelected = this.membershipSelected;
        membershipWithoutFormGroup.membershipDetailsExpanded = this.membershipDetailsExpanded;
        membershipWithoutFormGroup.membershipFulfilled = this.membershipFulfilled;

        membershipWithoutFormGroup.members = this.members;

        return (membershipWithoutFormGroup);
    }
}
